// Port of Offline/TileLayerDescriptor.swift (TrailBivvyKit): a raster XYZ tile layer (e.g. a geo.admin.ch WMTS layer
// in EPSG:3857) for display and offline storage, plus the coverage helpers of CoverageArea.swift's extension.
//
// Web additions (no Swift counterpart):
// - `corsEnabled`: the tile host answers with `Access-Control-Allow-Origin` for the app's origin (checked with curl on
//   2026-10-20, src/map/PROVIDERS.md). Only then can a page or service worker read a tile and store it in Cache
//   Storage; without CORS a tile is an opaque response that can be displayed by an <img> but never stored (Chrome
//   also charges ~7 MB of quota per opaque response).
// - `offlineCapable`: `allowsOfflineStorage && corsEnabled`, computed by `makeTileLayerDescriptor`. Offline packages
//   only ever contain offline-capable layers.

import type { GeoBounds, GeoPolygon } from '../core/models';
import { CoverageArea, coverageMarginMeters } from './CoverageArea';
import { tileIsValid, type TileCoordinate } from './TileMath';

export { coverageMarginMeters };

export interface TileLayerDescriptor {
  /** Stable layer identifier, e.g. "ch.swisstopo.pixelkarte-farbe". */
  id: string;
  /** URL template containing the placeholders `{z}`, `{x}` and `{y}`. */
  urlTemplate: string;
  /** File type of the tiles: "jpeg" or "png". */
  fileExtension: string;
  /** Lowest zoom level served by the source. */
  minZoom: number;
  /** Highest native zoom level served by the source (the map over-zooms beyond it). */
  maxZoom: number;
  /** Average tile size in bytes, used for download size estimates. */
  averageTileBytes: number;
  /** Attribution required by the data licence, e.g. "© swisstopo". */
  attribution: string;
  /** `true` for base maps that cover the whole tile (they hide what is drawn below them). */
  isOpaque: boolean;
  /** Whether the licence/terms allow storing tiles on the device for offline use. */
  allowsOfflineStorage: boolean;
  /** Web: the host sends CORS headers, so tiles can be read by script and cached (see the module comment). */
  corsEnabled: boolean;
  /** Web: `allowsOfflineStorage && corsEnabled` — the layer may be part of an offline package. */
  offlineCapable: boolean;
  /** Bounding box of the area where the source has data; undefined = worldwide (no limit). */
  coverage?: GeoBounds;
  /**
   * Exact area where the source has data (e.g. a country outline); undefined = the whole `coverage` box. A list without
   * any valid polygon is treated like undefined.
   */
  coveragePolygons?: GeoPolygon[];
}

export type TileLayerDescriptorInit = Omit<TileLayerDescriptor, 'offlineCapable' | 'corsEnabled'> & { corsEnabled?: boolean };

/** Builds a descriptor, deriving `offlineCapable`. `corsEnabled` defaults to `true` (every host checked so far). */
export function makeTileLayerDescriptor(init: TileLayerDescriptorInit): TileLayerDescriptor {
  const corsEnabled = init.corsEnabled ?? true;
  const layer: TileLayerDescriptor = {
    id: init.id,
    urlTemplate: init.urlTemplate,
    fileExtension: init.fileExtension,
    minZoom: init.minZoom,
    maxZoom: init.maxZoom,
    averageTileBytes: init.averageTileBytes,
    attribution: init.attribution,
    isOpaque: init.isOpaque,
    allowsOfflineStorage: init.allowsOfflineStorage,
    corsEnabled,
    offlineCapable: init.allowsOfflineStorage && corsEnabled,
  };
  if (init.coverage) layer.coverage = init.coverage;
  if (init.coveragePolygons) layer.coveragePolygons = init.coveragePolygons;
  return layer;
}

/** A copy with other fields; `offlineCapable` is recomputed. */
export function withLayerChanges(layer: TileLayerDescriptor, changes: Partial<TileLayerDescriptorInit>): TileLayerDescriptor {
  const merged = { ...layer, ...changes };
  const copy = makeTileLayerDescriptor(merged);
  if ('coverage' in changes && changes.coverage === undefined) delete copy.coverage;
  if ('coveragePolygons' in changes && changes.coveragePolygons === undefined) delete copy.coveragePolygons;
  return copy;
}

/** Native zoom levels of the source (tolerates swapped bounds). */
export function layerZoomRange(layer: TileLayerDescriptor): { min: number; max: number } {
  return { min: Math.min(layer.minZoom, layer.maxZoom), max: Math.max(layer.minZoom, layer.maxZoom) };
}

/**
 * The tile URL, or undefined when the tile address is invalid or the template does not form an absolute URL. Zoom
 * levels outside the native range still produce a URL; callers decide whether to request them.
 */
export function layerTileURL(layer: TileLayerDescriptor, tile: TileCoordinate): string | undefined {
  if (!tileIsValid(tile)) return undefined;
  const value = layer.urlTemplate
    .replaceAll('{z}', String(tile.z))
    .replaceAll('{x}', String(tile.x))
    .replaceAll('{y}', String(tile.y));
  if (/\s/.test(value)) return undefined;
  try {
    const url = new URL(value);
    if (!url.protocol || !url.host) return undefined;
  } catch {
    return undefined;
  }
  return value;
}

/**
 * Cheap sanity check that a downloaded body is an image of the declared format (magic bytes), so an HTML page from a
 * captive portal (hut or hotel Wi-Fi) is never stored as a map tile. Unknown extensions are accepted when non-empty.
 */
export function isPlausibleTileData(layer: TileLayerDescriptor, data: Uint8Array): boolean {
  if (data.length === 0) return false;
  const starts = (bytes: number[]) => bytes.every((b, i) => data[i] === b);
  switch (layer.fileExtension.toLowerCase()) {
    case 'png':
      return starts([0x89, 0x50, 0x4e, 0x47]);
    case 'jpeg':
    case 'jpg':
      return starts([0xff, 0xd8, 0xff]);
    case 'webp':
      return starts([0x52, 0x49, 0x46, 0x46]);
    default:
      return true;
  }
}

/** MIME type of the layer's tiles (used when a stored tile is served back). */
export function layerContentType(layer: TileLayerDescriptor): string {
  switch (layer.fileExtension.toLowerCase()) {
    case 'png':
      return 'image/png';
    case 'jpeg':
    case 'jpg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    default:
      return 'application/octet-stream';
  }
}

/** The declared coverage (`coverage` ∩ `coveragePolygons`). Build it once when testing many tiles. */
export function layerCoverageArea(layer: TileLayerDescriptor): CoverageArea {
  return new CoverageArea(layer.coverage, layer.coveragePolygons);
}

/** `true` when the layer declares no coverage limit. */
export function layerIsWorldwide(layer: TileLayerDescriptor): boolean {
  return layerCoverageArea(layer).isWorldwide;
}

/** Whether the tile is inside, or within `coverageMarginMeters` of, the layer's coverage. */
export function layerCoversTile(layer: TileLayerDescriptor, tile: TileCoordinate): boolean {
  return layerCoverageArea(layer).coversTile(tile);
}

/** Same descriptor (Swift `Equatable`), comparing every field including coverage geometry. */
export function sameLayer(a: TileLayerDescriptor, b: TileLayerDescriptor): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

function canonical(layer: TileLayerDescriptor): unknown[] {
  return [
    layer.id,
    layer.urlTemplate,
    layer.fileExtension,
    layer.minZoom,
    layer.maxZoom,
    layer.averageTileBytes,
    layer.attribution,
    layer.isOpaque,
    layer.allowsOfflineStorage,
    layer.corsEnabled,
    layer.coverage ?? null,
    layer.coveragePolygons ?? null,
  ];
}
